// Manual Booking — flight entry form (SMB-3541).
// Controlled: parent (ManualBooking page) owns the FlightForm state, passes it in
// as `form`, and receives immutable updates via `onChange`. Validation errors are
// path-keyed (see types.ts) and looked up by the same dotted paths used here.
import React from 'react';
import { Box, RadioGroup, FormControlLabel, Radio, Divider } from '@mui/material';
import {
  FlightForm,
  SectorForm,
  SegmentForm,
  PassengerForm,
  FieldErrors,
  AIRLINE_TYPES,
  PAX_TYPES,
  TITLES,
  JOURNEY_TYPES,
  emptySector,
  emptySegment,
  emptyPassenger,
} from './types';
import { Field, SelectField, CheckField, RowCard, AddButton, SectionTitle, SubTitle, errorText } from './fields';

interface Props {
  form: FlightForm;
  onChange: (next: FlightForm) => void;
  errors: FieldErrors;
}

const opt = (v: string, l?: string) => ({ value: v, label: l ?? v });

const ManualFlightForm: React.FC<Props> = ({ form, onChange, errors }) => {
  const update = (patch: Partial<FlightForm>) => onChange({ ...form, ...patch });

  // ---- amounts ----
  const setAmount = (k: keyof FlightForm['amounts'], v: string) =>
    update({ amounts: { ...form.amounts, [k]: v } });

  // ---- sectors / segments ----
  const setSectors = (sectors: SectorForm[]) => update({ sectors });
  const patchSector = (si: number, patch: Partial<SectorForm>) =>
    setSectors(form.sectors.map((s, i) => (i === si ? { ...s, ...patch } : s)));
  const patchSegment = (si: number, gi: number, patch: Partial<SegmentForm>) =>
    patchSector(si, {
      segments: form.sectors[si].segments.map((g, i) => (i === gi ? { ...g, ...patch } : g)),
    });
  const addSector = () => setSectors([...form.sectors, emptySector()]);
  const removeSector = (si: number) => setSectors(form.sectors.filter((_, i) => i !== si));
  const addSegment = (si: number) =>
    patchSector(si, { segments: [...form.sectors[si].segments, emptySegment()] });
  const removeSegment = (si: number, gi: number) =>
    patchSector(si, { segments: form.sectors[si].segments.filter((_, i) => i !== gi) });

  // ---- passengers ----
  const setPassengers = (passengers: PassengerForm[]) => update({ passengers });
  const patchPassenger = (pi: number, patch: Partial<PassengerForm>) =>
    setPassengers(form.passengers.map((p, i) => (i === pi ? { ...p, ...patch } : p)));
  const patchDoc = (pi: number, k: string, v: string) =>
    patchPassenger(pi, { document: { ...form.passengers[pi].document, [k]: v } });
  const patchOps = (pi: number, k: string, v: string) =>
    patchPassenger(pi, { operations: { ...form.passengers[pi].operations, [k]: v } });
  const patchInfant = (pi: number, patch: Partial<PassengerForm['infant']>) =>
    patchPassenger(pi, { infant: { ...form.passengers[pi].infant, ...patch } });
  const patchInfantDoc = (pi: number, k: string, v: string) =>
    patchInfant(pi, { document: { ...form.passengers[pi].infant.document, [k]: v } });
  const setLead = (pi: number) =>
    setPassengers(form.passengers.map((p, i) => ({ ...p, leadPax: i === pi })));
  const addPassenger = () => setPassengers([...form.passengers, emptyPassenger(false)]);
  const removePassenger = (pi: number) =>
    setPassengers(form.passengers.filter((_, i) => i !== pi));

  return (
    <Box>
      {/* ------------------------------------------------ Amounts */}
      <SectionTitle>Amounts (what the customer paid)</SectionTitle>
      <Box className="feeInputFieldWrap">
        <Field
          label="Total Paid"
          type="number"
          required
          value={form.amounts.totalUsrAmt}
          onChange={(v) => setAmount('totalUsrAmt', v)}
          error={errors['amounts.totalUsrAmt']}
        />
        <Field
          label="Supplier Total"
          type="number"
          required
          value={form.amounts.supTotalAmt}
          onChange={(v) => setAmount('supTotalAmt', v)}
          error={errors['amounts.supTotalAmt']}
        />
        <Field
          label="USD Total"
          type="number"
          required
          value={form.amounts.usCurTotalAmt}
          onChange={(v) => setAmount('usCurTotalAmt', v)}
          error={errors['amounts.usCurTotalAmt']}
        />
        <Field
          label="Currency"
          required
          value={form.amounts.currency}
          onChange={(v) => setAmount('currency', v)}
          error={errors['amounts.currency']}
          placeholder="USD"
        />
        <Field
          label="Flight Price (optional)"
          type="number"
          value={form.amounts.productPrice}
          onChange={(v) => setAmount('productPrice', v)}
          error={errors['amounts.productPrice']}
          placeholder="defaults to Total Paid"
        />
        <SelectField
          label="Trip Type"
          value={form.tripType}
          onChange={(v) => update({ tripType: v as FlightForm['tripType'] })}
          options={[
            opt('', 'Auto (from sectors)'),
            opt('1', 'One-way'),
            opt('2', 'Round-trip'),
            opt('3', 'Multi-city'),
          ]}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* ------------------------------------------------ Sectors */}
      <SectionTitle>Sectors</SectionTitle>
      {form.sectors.map((s, si) => (
        <RowCard
          key={si}
          title={`Sector ${si + 1}`}
          onRemove={() => removeSector(si)}
          removeDisabled={form.sectors.length === 1}
        >
          <Box className="feeInputFieldWrap">
            <Field label="Source (IATA)" required value={s.source}
              onChange={(v) => patchSector(si, { source: v })} error={errors[`sectors.${si}.source`]} placeholder="JFK" />
            <Field label="Destination (IATA)" required value={s.destination}
              onChange={(v) => patchSector(si, { destination: v })} error={errors[`sectors.${si}.destination`]} placeholder="LHR" />
            <Field label="PNR" required value={s.pnr}
              onChange={(v) => patchSector(si, { pnr: v })} error={errors[`sectors.${si}.pnr`]} placeholder="ABC123" />
            <Field label="Cabin Class" value={s.cabinClass}
              onChange={(v) => patchSector(si, { cabinClass: v })} placeholder="ECONOMY" />
            <SelectField label="Journey Type" value={s.journeyType}
              onChange={(v) => patchSector(si, { journeyType: v })}
              options={JOURNEY_TYPES.map((j) => opt(j, j === '' ? 'Auto (by order)' : j))} />
            <SelectField label="Airline Type" value={s.airlineType}
              onChange={(v) => patchSector(si, { airlineType: v as SectorForm['airlineType'] })}
              options={AIRLINE_TYPES.map((a) => opt(a))} />
            <Field label="Supplier Base Fare" type="number" required value={s.supBaseFare}
              onChange={(v) => patchSector(si, { supBaseFare: v })} error={errors[`sectors.${si}.supBaseFare`]} />
            <Field label="Supplier Taxes" type="number" required value={s.supTaxes}
              onChange={(v) => patchSector(si, { supTaxes: v })} error={errors[`sectors.${si}.supTaxes`]} />
            <Field label="Supplier Total Fare" type="number" required value={s.supTotalFare}
              onChange={(v) => patchSector(si, { supTotalFare: v })} error={errors[`sectors.${si}.supTotalFare`]} />
          </Box>
          <Box mt={1}>
            <CheckField label="Refundable" checked={s.isRefundable}
              onChange={(v) => patchSector(si, { isRefundable: v })} />
          </Box>

          {/* Segments */}
          <SubTitle>Segments</SubTitle>
          {errors[`sectors.${si}.segments`] && (
            <span style={errorText}>{errors[`sectors.${si}.segments`]}</span>
          )}
          {s.segments.map((g, gi) => (
            <RowCard
              key={gi}
              title={`Segment ${gi + 1}`}
              tone="nested"
              onRemove={() => removeSegment(si, gi)}
              removeDisabled={s.segments.length === 1}
            >
              <Box className="feeInputFieldWrap">
                <Field label="From (IATA)" required value={g.segSource}
                  onChange={(v) => patchSegment(si, gi, { segSource: v })} error={errors[`sectors.${si}.segments.${gi}.segSource`]} />
                <Field label="To (IATA)" required value={g.segDestination}
                  onChange={(v) => patchSegment(si, gi, { segDestination: v })} error={errors[`sectors.${si}.segments.${gi}.segDestination`]} />
                <Field label="Marketing Airline" required value={g.marketingAirline}
                  onChange={(v) => patchSegment(si, gi, { marketingAirline: v })} error={errors[`sectors.${si}.segments.${gi}.marketingAirline`]} placeholder="BA" />
                <Field label="Operating Airline" value={g.operatingAirline}
                  onChange={(v) => patchSegment(si, gi, { operatingAirline: v })} placeholder="defaults to marketing" />
                <Field label="Flight No" required value={g.flightNo}
                  onChange={(v) => patchSegment(si, gi, { flightNo: v })} error={errors[`sectors.${si}.segments.${gi}.flightNo`]} placeholder="178" />
                <Field label="Depart" type="datetime-local" required value={g.departTime}
                  onChange={(v) => patchSegment(si, gi, { departTime: v })} error={errors[`sectors.${si}.segments.${gi}.departTime`]} />
                <Field label="Arrive" type="datetime-local" required value={g.arrivalTime}
                  onChange={(v) => patchSegment(si, gi, { arrivalTime: v })} error={errors[`sectors.${si}.segments.${gi}.arrivalTime`]} />
                <Field label="Booking Class (RBD)" value={g.segClass}
                  onChange={(v) => patchSegment(si, gi, { segClass: v })} placeholder="Y / J / F" />
                <Field label="Depart Terminal" value={g.departTerminal}
                  onChange={(v) => patchSegment(si, gi, { departTerminal: v })} />
                <Field label="Arrive Terminal" value={g.arrivalTerminal}
                  onChange={(v) => patchSegment(si, gi, { arrivalTerminal: v })} />
                <Field label="Airline PNR" value={g.airlinePnr}
                  onChange={(v) => patchSegment(si, gi, { airlinePnr: v })} placeholder="defaults to sector PNR" />
                <Field label="Free Baggage" value={g.freeBaggage}
                  onChange={(v) => patchSegment(si, gi, { freeBaggage: v })} />
                <Field label="Cabin Baggage" value={g.cabinBaggage}
                  onChange={(v) => patchSegment(si, gi, { cabinBaggage: v })} />
              </Box>
            </RowCard>
          ))}
          <AddButton label="Add segment" onClick={() => addSegment(si)} />
        </RowCard>
      ))}
      <AddButton label="Add sector" onClick={addSector} />

      <Divider sx={{ my: 2 }} />

      {/* ------------------------------------------------ Passengers */}
      <SectionTitle>Passengers</SectionTitle>
      {errors._leadPax && <span style={errorText}>{errors._leadPax}</span>}
      {form.passengers.map((p, pi) => (
        <RowCard
          key={pi}
          title={`Passenger ${pi + 1}${p.leadPax ? ' (lead)' : ''}`}
          onRemove={() => removePassenger(pi)}
          removeDisabled={form.passengers.length === 1}
        >
          <RadioGroup row value={p.leadPax ? 'lead' : ''} onChange={() => setLead(pi)}>
            <FormControlLabel
              value="lead"
              control={<Radio sx={{ color: '#272727', '&.Mui-checked': { color: '#272727' } }} />}
              label="Lead passenger"
            />
          </RadioGroup>
          <Box className="feeInputFieldWrap">
            <SelectField label="Pax Type" value={p.paxType}
              onChange={(v) => patchPassenger(pi, { paxType: v as PassengerForm['paxType'] })}
              options={PAX_TYPES.map((t) => opt(t))} />
            <SelectField label="Title" required value={p.title}
              onChange={(v) => patchPassenger(pi, { title: v })}
              options={[opt('', 'Select'), ...TITLES.map((t) => opt(t))]} error={errors[`passengers.${pi}.title`]} />
            <Field label="First Name" required value={p.firstName}
              onChange={(v) => patchPassenger(pi, { firstName: v })} error={errors[`passengers.${pi}.firstName`]} />
            <Field label="Middle Name" value={p.middleName}
              onChange={(v) => patchPassenger(pi, { middleName: v })} />
            <Field label="Last Name" required value={p.lastName}
              onChange={(v) => patchPassenger(pi, { lastName: v })} error={errors[`passengers.${pi}.lastName`]} />
            <Field label="Date of Birth" type="date" required value={p.dateOfBirth}
              onChange={(v) => patchPassenger(pi, { dateOfBirth: v })} error={errors[`passengers.${pi}.dateOfBirth`]} />
            <Field label="Country Name" value={p.countryName}
              onChange={(v) => patchPassenger(pi, { countryName: v })} />
            <Field label="ISO" value={p.iso}
              onChange={(v) => patchPassenger(pi, { iso: v })} placeholder="US" />
          </Box>

          {p.leadPax && (
            <>
              <SubTitle>Lead contact</SubTitle>
              <Box className="feeInputFieldWrap">
                <Field label="Email" required value={p.email}
                  onChange={(v) => patchPassenger(pi, { email: v })} error={errors[`passengers.${pi}.email`]} />
                <Field label="Country Code" required value={p.countryCode}
                  onChange={(v) => patchPassenger(pi, { countryCode: v })} error={errors[`passengers.${pi}.countryCode`]} placeholder="1" />
                <Field label="Phone Number" required value={p.phoneNumber}
                  onChange={(v) => patchPassenger(pi, { phoneNumber: v })} error={errors[`passengers.${pi}.phoneNumber`]} />
              </Box>
            </>
          )}

          {/* Per-pax fares — Total Fare is required (engine total_fare is NOT NULL). */}
          <SubTitle>Fares (per passenger)</SubTitle>
          <Box className="feeInputFieldWrap">
            <Field label="Total Fare" type="number" required value={p.operations.totalFare}
              onChange={(v) => patchOps(pi, 'totalFare', v)} error={errors[`passengers.${pi}.operations.totalFare`]} />
            <Field label="Base Fare" type="number" value={p.operations.baseFare}
              onChange={(v) => patchOps(pi, 'baseFare', v)} error={errors[`passengers.${pi}.operations.baseFare`]}
              placeholder="defaults to Total Fare" />
            <Field label="Supplier Total Fare" type="number" value={p.operations.supplierTotalFare}
              onChange={(v) => patchOps(pi, 'supplierTotalFare', v)} />
            <Field label="Supplier Base Fare" type="number" value={p.operations.supplierBaseFare}
              onChange={(v) => patchOps(pi, 'supplierBaseFare', v)} />
            <Field label="Markup Amount" type="number" value={p.operations.markupAmount}
              onChange={(v) => patchOps(pi, 'markupAmount', v)} />
            <Field label="Service Fee Amount" type="number" value={p.operations.servicefeeAmount}
              onChange={(v) => patchOps(pi, 'servicefeeAmount', v)} />
          </Box>

          {/* Document (optional) */}
          <Box mt={1}>
            <CheckField label="Add travel document"
              checked={p.hasDocument} onChange={(v) => patchPassenger(pi, { hasDocument: v })} />
          </Box>
          {p.hasDocument && (
            <Box className="feeInputFieldWrap">
              <Field label="Doc Type" required value={p.document.type}
                onChange={(v) => patchDoc(pi, 'type', v)} error={errors[`passengers.${pi}.document.type`]} placeholder="Passport" />
              <Field label="Doc Number" required value={p.document.number}
                onChange={(v) => patchDoc(pi, 'number', v)} error={errors[`passengers.${pi}.document.number`]} />
              <Field label="Nationality" value={p.document.nationality}
                onChange={(v) => patchDoc(pi, 'nationality', v)} placeholder="US" />
              <Field label="Issued Country" value={p.document.issuedCountry}
                onChange={(v) => patchDoc(pi, 'issuedCountry', v)} />
              <Field label="Issued Date" type="date" value={p.document.issuedDate}
                onChange={(v) => patchDoc(pi, 'issuedDate', v)} error={errors[`passengers.${pi}.document.issuedDate`]} />
              <Field label="Expiry Date" type="date" value={p.document.expiryDate}
                onChange={(v) => patchDoc(pi, 'expiryDate', v)} error={errors[`passengers.${pi}.document.expiryDate`]} />
            </Box>
          )}

          {/* Lap infant (optional) */}
          <Box mt={1}>
            <CheckField label="Carrying a lap infant"
              checked={p.hasInfant} onChange={(v) => patchPassenger(pi, { hasInfant: v })} />
          </Box>
          {p.hasInfant && (
            <RowCard title="Infant" tone="nested">
              <Box className="feeInputFieldWrap">
                <SelectField label="Title" required value={p.infant.title}
                  onChange={(v) => patchInfant(pi, { title: v })}
                  options={[opt('', 'Select'), ...TITLES.map((t) => opt(t))]} error={errors[`passengers.${pi}.infant.title`]} />
                <Field label="First Name" required value={p.infant.firstName}
                  onChange={(v) => patchInfant(pi, { firstName: v })} error={errors[`passengers.${pi}.infant.firstName`]} />
                <Field label="Last Name" required value={p.infant.lastName}
                  onChange={(v) => patchInfant(pi, { lastName: v })} error={errors[`passengers.${pi}.infant.lastName`]} />
                <Field label="Date of Birth" type="date" required value={p.infant.dateOfBirth}
                  onChange={(v) => patchInfant(pi, { dateOfBirth: v })} error={errors[`passengers.${pi}.infant.dateOfBirth`]} />
              </Box>
              <Box mt={1}>
                <CheckField label="Add infant document"
                  checked={p.infant.hasDocument} onChange={(v) => patchInfant(pi, { hasDocument: v })} />
              </Box>
              {p.infant.hasDocument && (
                <Box className="feeInputFieldWrap">
                  <Field label="Doc Type" required value={p.infant.document.type}
                    onChange={(v) => patchInfantDoc(pi, 'type', v)} error={errors[`passengers.${pi}.infant.document.type`]} placeholder="Passport" />
                  <Field label="Doc Number" required value={p.infant.document.number}
                    onChange={(v) => patchInfantDoc(pi, 'number', v)} error={errors[`passengers.${pi}.infant.document.number`]} />
                  <Field label="Nationality" value={p.infant.document.nationality}
                    onChange={(v) => patchInfantDoc(pi, 'nationality', v)} />
                </Box>
              )}
              <SubTitle>Infant Fares</SubTitle>
              <Box className="feeInputFieldWrap">
                <Field label="Total Fare" type="number" value={p.infant.operations.totalFare}
                  onChange={(v) => patchInfant(pi, { operations: { ...p.infant.operations, totalFare: v } })} />
                <Field label="Base Fare" type="number" value={p.infant.operations.baseFare}
                  onChange={(v) => patchInfant(pi, { operations: { ...p.infant.operations, baseFare: v } })}
                  placeholder="defaults to Total Fare" />
              </Box>
            </RowCard>
          )}
        </RowCard>
      ))}
      <AddButton label="Add passenger" onClick={addPassenger} />
    </Box>
  );
};

export default ManualFlightForm;
